import '@/styles/globals.scss';
import 'react-responsive-modal/styles.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-datepicker/src/stylesheets/datepicker.scss';
import '@/styles/datepicker.scss';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useEffect, useState } from 'react';
import axiosInstance from '@/actions/axiosConfig';
import { GET_ENV_VARIABLES } from '@/configs/apiEndPoints';
import { AxiosResponse } from 'axios';
import { storeEnvToLocalStorage } from '@/utils/envUtils';
import { FeaturesType, fetchFeatures } from '@/actions/fetchFeatures';
import is from '@/utils/is';
import Fonts from '@/components/Fonts';
import Layout from '@/components/Layouts/Layout';
import { fetchAppConfig } from '@/actions/fetchAppConfig';
import Providers from '@/context/Providers';
import { CustomAppProps } from '@/types';
import { getProfileV2 } from '@/actions/fetchProfile';
import { CLIENT_COOKIE, SESSION_COOKIE } from '@/configs/serviceConstants';
import Cookies from '@/utils/cookie';
import { getFooterDetail } from '@/actions/ecom/footer';
import { getHeaderDetail } from '@/actions/ecom/header';
import isAuthenticatedRoute from '@/utils/isAuthenticatedRoute';
import dynamic from 'next/dynamic';
import { usePageViewed } from '@/hooks/useEcomGTM';
import { useRouter } from 'next/router';
import { SCA_COOKIE } from '@/configs/constants';

const RouteProgressbar = dynamic(
  () => import(/* webpackChunkName: "app-lazy-ssr-chunks" */ '@/components/RouteProgressbar'),
  {
    ssr: false,
  },
);

const CustomApp = ({
  Component,
  pageProps,
  router,
  config,
  profile,
  host,
}: CustomAppProps & AppProps) => {
  const [featureFlags, setFeatureFlags] = useState<FeaturesType>({});
  const { pathname } = useRouter();
  const [location, setLocation] = useState<string | null>(null);

  useEffect(() => {
    const fetchEnv = async () => {
      const response: AxiosResponse<{ [key: string]: string }> = await axiosInstance.get(
        GET_ENV_VARIABLES,
      );
      const city = response.headers['x-user-city'];
      const lat = response.headers['x-user-lat'];
      const lng = response.headers['x-user-long'];
      if (city && lat && lng) {
        setLocation(`${city}:${lat}:${lng}`);
      }
      storeEnvToLocalStorage(response.data);
    };
    fetchEnv();
  }, []);

  const getFeatures = async () => {
    try {
      const features = await fetchFeatures();
      setFeatureFlags(features);
    } catch (error) {
      setFeatureFlags({});
    }
  };

  usePageViewed(router && router.pathname ? router.pathname : '', config);

  useEffect(() => {
    if (isAuthenticatedRoute(pathname) && is.empty.object(featureFlags)) {
      getFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <ErrorBoundary>
      <Fonts />
      <RouteProgressbar />
      <Providers
        profile={profile}
        config={{ ...config, location }}
        pageProps={pageProps}
        featureFlags={featureFlags}
        Component={Component}
        router={router}
      >
        <Layout
          pageProps={pageProps}
          Component={Component}
          router={router}
          host={host}
          location={location}
        />
      </Providers>
    </ErrorBoundary>
  );
};

CustomApp.getInitialProps = async (
  context: AppContext,
): Promise<CustomAppProps & AppInitialProps> => {
  let config: any = {};
  if (Cookies.get(CLIENT_COOKIE, context.ctx.req?.headers?.cookie) === 'flutter') {
    config['isFlutterClient'] = true;
  } else if (Cookies.get(CLIENT_COOKIE, context.ctx.req?.headers?.cookie) === SCA_COOKIE) {
    config['isSCAClient'] = true;
    if (Cookies.get('platform', context.ctx.req?.headers?.cookie)) {
      config['platform'] = Cookies.get('platform', context.ctx.req?.headers?.cookie);
    }
  }

  const ctx = await App.getInitialProps(context);

  let promises: Promise<any>[] = [fetchAppConfig()];

  if (config?.isSCAClient) {
    promises.push(Promise.resolve({}));
    promises.push(Promise.resolve({}));
  } else {
    promises.push(getHeaderDetail());
    promises.push(getFooterDetail());
  }

  if (Cookies.has(SESSION_COOKIE, context.ctx.req?.headers?.cookie)) {
    promises.push(getProfileV2(context.ctx.req?.headers.cookie, config?.['isSCAClient']));
  } else {
    promises.push(Promise.resolve(null));
  }

  let profile: any = {};

  try {
    const [appConfig, header, footer, userProfile] = await Promise.allSettled(promises);
    if (userProfile?.status === 'fulfilled') profile = userProfile.value;
    if (appConfig.status === 'fulfilled') config = { ...config, ...appConfig.value };
    if (footer.status === 'fulfilled') config.footer = footer.value;
    if (header.status === 'fulfilled') config.header = header.value;
  } catch (error) {
    console.error(error);
    // config = {};
  }

  return { ...ctx, config, profile, host: context.ctx.req?.headers.host };
};

export default CustomApp;
